<script setup lang="ts">
import { computed } from 'vue';

import type { CurrencyCode } from '@/__generated__/storefront/graphql';
import { FreeBadge, PlusBadge, SaleBadge } from '@/shared/components/UI/badges';
import Image from '@/shared/components/UI/Image.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
// Badge components removed - using inline styling for cleaner look
import type { HydratedYarnPattern } from '@/shared/composables/yarnPatterns/yarnPatterns.types';
import { formatPriceStorefront } from '@/shared/utils/shopify/money-formatter';

const props = defineProps<{
  pattern: HydratedYarnPattern;
}>();

const { t } = useTranslations();

// Pattern title - prefer product title over gateway name
const patternTitle = computed(() => {
  return (
    props.pattern.product?.title || props.pattern.name || props.pattern.sku
  );
});

// Image handling - similar to PatternProductCard
const patternImage = computed(() => {
  const url =
    props.pattern.product?.image?.url || props.pattern.gatewayImage?.url;
  if (url) {
    const imgSrc = new URL(url);
    imgSrc.searchParams.set('width', '400');
    imgSrc.searchParams.set('height', '400');
    imgSrc.searchParams.set('crop', 'center');
    return imgSrc.toString();
  }
  return '';
});

const patternImageAlt = computed(() => {
  return (
    props.pattern.product?.image?.altText ||
    props.pattern.gatewayImage?.alt ||
    patternTitle.value
  );
});

// URL generation - prefer product handle like PatternProductCard
const patternUrl = computed(() => {
  if (props.pattern.product?.handle) {
    return `/products/${props.pattern.product.handle}`;
  }
  // Fallback to SKU-based URL if no product handle
  return props.pattern.sku ? `/products/${props.pattern.sku}` : '#';
});

// Pattern metadata
const difficulty = computed(() => {
  return props.pattern.product?.difficulty;
});

const patternKind = computed(() => {
  return props.pattern.product?.patternKind;
});

// Pricing logic - similar to liquid card logic
const price = computed(() => {
  return props.pattern.product?.price;
});

const compareAtPrice = computed(() => {
  return props.pattern.product?.compareAtPrice;
});

const priceWithCurrencySymbol = computed(() => {
  if (!price.value) return '';
  // Convert ProductPrice to MoneyV2 format
  const moneyV2 = {
    amount: price.value.amount.toString(),
    currencyCode: price.value.currencyCode as CurrencyCode
  };
  return formatPriceStorefront(moneyV2);
});

const compareAtPriceWithCurrencySymbol = computed(() => {
  if (!compareAtPrice.value || !compareAtPrice.value.amount) return '';
  // Convert ProductPrice to MoneyV2 format
  const moneyV2 = {
    amount: compareAtPrice.value.amount.toString(),
    currencyCode: compareAtPrice.value.currencyCode as CurrencyCode
  };
  return formatPriceStorefront(moneyV2);
});

const hasDiscount = computed(() => {
  return (
    compareAtPrice.value &&
    compareAtPrice.value.amount > (price.value?.amount || 0)
  );
});

const discountPercentage = computed(() => {
  if (!hasDiscount.value) return 0;
  const original = parseFloat(compareAtPrice.value!.amount.toString());
  const current = parseFloat(price.value!.amount.toString());
  return Math.floor(((original - current) / original) * 100);
});

// Badge logic - matching liquid card logic exactly
const isPattern = computed(() => {
  // Check if this is a pattern (assuming we can detect this from the data)
  return true; // All yarn patterns are patterns
});

const priceInPoints = computed(() => {
  // For yarn patterns, assume no loyalty points required
  return 0;
});

const showFreePatternBadge = computed(() => {
  const priceAmount = price.value?.amount;
  const isFree = priceAmount === 0;

  return (
    isPattern.value &&
    priceInPoints.value <= 0 &&
    props.pattern.product?.id &&
    isFree
  );
});

const showPlusPatternBadge = computed(() => {
  const priceAmount = price.value?.amount;
  const isFree = priceAmount === 0;

  return (
    isPattern.value &&
    priceInPoints.value <= 0 &&
    props.pattern.product?.id &&
    !isFree
  );
});
</script>

<template>
  <!-- Structure matches liquid card-product.liquid layout -->
  <a
    :href="patternUrl"
    class="relative -m-2 flex flex-col gap-4 rounded-lg p-2 transition-opacity hover:opacity-80"
  >
    <!-- Product Image with Badges (matches liquid card structure) -->
    <div class="relative">
      <Image
        v-if="patternImage"
        :src="patternImage"
        :alt="patternImageAlt"
        :width="400"
        :height="400"
        img-classes="aspect-square w-full object-cover transition-transform duration-200 hover:scale-105"
        class="overflow-hidden rounded-lg bg-gray-100"
        :lazy-load="true"
        loader="fade"
      />
      <div
        v-else
        class="flex aspect-square w-full items-center justify-center rounded-lg bg-gray-100"
      >
        <span class="text-sm text-gray-400">{{
          t('general.missing_image_alt')
        }}</span>
      </div>

      <!-- Badges Overlay - matches liquid card badge positioning -->
      <div class="absolute left-0 top-0 flex w-full justify-between">
        <div class="flex flex-col items-start gap-0.5 p-2">
          <!-- Free Pattern Badge -->
          <FreeBadge v-if="showFreePatternBadge" :show-icon="true" />

          <!-- Plus Pattern Badge -->
          <PlusBadge v-else-if="showPlusPatternBadge" />
        </div>

        <!-- Sale Badge - matches liquid card positioning -->
        <div v-if="hasDiscount" class="flex flex-col items-end gap-0.5 p-2">
          <SaleBadge :percentage="discountPercentage" />
        </div>
      </div>
    </div>

    <!-- Product Info - matches liquid card info section -->
    <div class="flex flex-col gap-2 text-center leading-tight">
      <!-- Title -->
      <h3 class="line-clamp-2 text-sm font-semibold leading-snug">
        {{ patternTitle }}
      </h3>

      <!-- Difficulty and Pattern Type (matching liquid layout) -->
      <div class="text-xs text-gray-600">
        <!-- Pattern type badge first, then difficulty -->
        <div
          v-if="patternKind || difficulty"
          class="flex items-center justify-center gap-1"
        >
          <!-- Pattern type badge (crochet/knitting) on the left -->
          <span
            v-if="patternKind"
            class="rounded bg-gray-100 px-2 py-1 text-xs capitalize text-gray-600"
          >
            {{ patternKind }}
          </span>
          <span v-if="difficulty" class="capitalize">{{ difficulty }}</span>
        </div>
      </div>

      <!-- Pricing - matches liquid card pricing structure -->
      <div v-if="price" class="flex items-center justify-center gap-2">
        <span
          class="text-base font-bold"
          :class="hasDiscount ? 'text-red-600' : 'text-black'"
        >
          <!-- Show "Free" for free patterns instead of "0 kr" -->
          <template v-if="price.amount === 0">
            <span class="capitalize">{{
              t('patterns.products.pattern.free')
            }}</span>
          </template>
          <template v-else>
            {{ priceWithCurrencySymbol }}
          </template>
        </span>
        <span v-if="hasDiscount" class="text-sm text-gray-500 line-through">
          {{ compareAtPriceWithCurrencySymbol }}
        </span>
      </div>
    </div>
  </a>
</template>
